import { useCallback, useEffect, useMemo, useState } from 'react';

import { ko } from 'date-fns/locale';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import calendarIcon from '@shared/assets/images/calendar_icon.svg';
import chevronDownSmIcon from '@shared/assets/images/chevron-down-sm_icon.svg';
import useAlert from '@features/document-write/hooks/useAlert';
import { useReferenceDocListQuery } from '@features/document-write/hooks';
import Alert from '@shared/components/atoms/Alert/Alert';

import styles from './ReferenceDocumentModal.module.scss';

/**
 * ReferenceDocumentModal 컴포넌트
 * - 참조문서 선택 모달
 * - 피그마 디자인 기반 (node-id=1241:7596)
 * - 피그마 기획 기반 (node-id=722:3514)
 * - SearchFilter 공용 컴포넌트 사용
 * - 테이블은 피그마 디자인에 맞춘 커스텀 구조 사용 (컬럼별 세로 배치)
 */
function ReferenceDocumentModal({
  isOpen,
  onClose,
  onSelect,
  selectedDocuments = [],
  onDocumentRowClick,
}) {
  const { alertState, showAlert, closeAlert } = useAlert();

  // 3개월 전 ~ 당일 날짜 계산 (useMemo로 최적화)
  const defaultDates = useMemo(() => {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    function formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return {
      date1: formatDate(threeMonthsAgo),
      date2: formatDate(today),
    };
  }, []);

  // 필터 값 상태
  const [filterValues, setFilterValues] = useState({
    date1: defaultDates.date1,
    date2: defaultDates.date2,
    searchType: 0,
    search: '',
  });

  // 실제 검색에 사용되는 파라미터 (검색 버튼 클릭 시 업데이트됨)
  // stateCode: 3 = 결재완료 (참조문서는 결재완료된 문서만 표시)
  const [searchParams, setSearchParams] = useState({
    date1: defaultDates.date1,
    date2: defaultDates.date2,
    searchType: 0,
    search: '',
    stateCode: 3,
  });

  // 체크박스로 선택한 문서들 (추가 버튼 클릭 전까지 임시 저장)
  const [checkedDocuments, setCheckedDocuments] = useState([]);

  // 선택된 문서들 (최신순 정렬)
  const [tempSelected, setTempSelected] = useState([...selectedDocuments]);

  // 참조문서 목록 조회 - 모달이 열릴 때만 API 호출
  const { data: referenceDocList = [], isLoading } = useReferenceDocListQuery(searchParams, {
    enabled: isOpen, // 모달이 열려있을 때만 쿼리 실행
  });

  // 모달이 열릴 때 기본 조회 실행 및 body 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      // 기본 날짜 범위로 초기 조회 실행
      setSearchParams({
        date1: defaultDates.date1,
        date2: defaultDates.date2,
        searchType: 0,
        search: '',
        stateCode: 3,
      });
      // 필터 값도 기본값으로 초기화
      setFilterValues({
        date1: defaultDates.date1,
        date2: defaultDates.date2,
        searchType: 0,
        search: '',
      });

      // body 스크롤 방지
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      // cleanup: 모달이 닫힐 때 원래 스크롤 상태로 복원
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen, defaultDates]);

  // 선택된 문서의 idx 목록
  const selectedIdxList = useMemo(() => {
    return tempSelected.map((doc) => doc.idx);
  }, [tempSelected]);

  // 문서 목록에서 선택된 문서 제외 (중복 선택 방지)
  // 문서 목록 (기존에는 선택된 문서를 제외했으나, 이제 제외하지 않음)
  const availableDocList = referenceDocList;

  // 문자열 → Date 객체 변환
  const toDate = useCallback((str) => {
    if (!str) return null;
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
  }, []);

  // Date 객체 → YYYY-MM-DD 문자열 변환
  const formatDate = useCallback((date) => {
    if (!date) return '';
    try {
      return date.toISOString().slice(0, 10);
    } catch {
      return '';
    }
  }, []);

  // 필터 값 변경 핸들러
  const handleFilterChange = useCallback((key, value) => {
    setFilterValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // 검색 핸들러
  const handleSearch = useCallback(() => {
    setSearchParams({
      date1: filterValues.date1,
      date2: filterValues.date2,
      searchType: filterValues.searchType,
      search: filterValues.search || '',
      stateCode: 3,
    });
    // 검색 시 체크박스 초기화
    setCheckedDocuments([]);
  }, [filterValues]);

  // 문서 체크박스 토글 핸들러
  const handleCheckboxToggle = useCallback((doc) => {
    setCheckedDocuments((prev) => {
      const exists = prev.find((item) => item.idx === doc.idx);
      if (exists) {
        return prev.filter((item) => item.idx !== doc.idx);
      }
      return [...prev, doc];
    });
  }, []);

  // 추가 버튼 핸들러 (체크박스로 선택한 문서들을 선택된 문서로 이동)
  const handleAdd = useCallback(() => {
    if (checkedDocuments.length === 0) {
      showAlert('문서를 선택해 주세요.');
      return;
    }

    // 이미 선택된 문서는 제외하고 새로 추가할 문서만 필터링
    const newDocs = checkedDocuments.filter((doc) => !selectedIdxList.includes(doc.idx));

    if (newDocs.length === 0) {
      showAlert('이미 추가된 문서입니다.');
      setCheckedDocuments([]);
      return;
    }

    // 최신순으로 정렬 (가장 최근에 추가한 문서가 맨 위)
    setTempSelected((prev) => [...newDocs, ...prev]);
    setCheckedDocuments([]); // 체크박스 초기화
  }, [checkedDocuments, selectedIdxList, showAlert]);

  // 선택된 문서 삭제 핸들러 (제거된 문서는 문서 목록에 다시 표시됨)
  const handleRemoveSelected = useCallback((doc) => {
    setTempSelected((prev) => prev.filter((item) => item.idx !== doc.idx));
  }, []);

  // 문서 목록 행 클릭 핸들러 (문서상세보기 모달 열기용)
  const handleDocumentRowClick = useCallback(
    (doc) => {
      if (onDocumentRowClick && doc?.idx != null) {
        onDocumentRowClick(doc);
      }
    },
    [onDocumentRowClick],
  );

  // 저장 핸들러
  const handleSave = useCallback(() => {
    // 사용자가 모든 선택을 취소하고 저장을 누를 수 있도록 수정
    if (onSelect) {
      onSelect(tempSelected);
    }
    onClose();
  }, [tempSelected, onSelect, onClose]);

  // 모달이 닫혀있으면 렌더링하지 않음 (모든 훅 호출 후에 early return)
  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          {/* 모달 헤더 */}
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>참조 문서 선택</h2>
          </div>

          {/* 모달 본문 */}
          <div className={styles.modalBody}>
            {/* 검색 필드 영역 - Figma 디자인 기반 직접 구현 */}
            <div className={styles.searchFieldsContainer}>
              {/* 상단 테두리 */}
              <div className={styles.filterTopBorder} />

              {/* 첫 번째 행: 조회기간 */}
              <div className={styles.filterRow}>
                <div className={styles.filterLabel}>조회기간</div>
                <div className={styles.dateRangeContainer}>
                  <div className={styles.dateInputWrapper}>
                    <DatePicker
                      selected={toDate(filterValues.date1)}
                      onChange={(date) => {
                        handleFilterChange('date1', formatDate(date));
                      }}
                      locale={ko}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="2025/09/01"
                      className={styles.dateInput}
                    />
                    <img src={calendarIcon} alt="" className={styles.calendarIcon} />
                  </div>
                  <span className={styles.dateSeparator}>~</span>
                  <div className={styles.dateInputWrapper}>
                    <DatePicker
                      selected={toDate(filterValues.date2)}
                      onChange={(date) => {
                        handleFilterChange('date2', formatDate(date));
                      }}
                      locale={ko}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="2025/11/30"
                      className={styles.dateInput}
                    />
                    <img src={calendarIcon} alt="" className={styles.calendarIcon} />
                  </div>
                </div>
              </div>

              {/* 구분선 */}
              <div className={styles.filterDivider} />

              {/* 두 번째 행: 조회구분 */}
              <div className={styles.filterRow}>
                <div className={styles.filterLabel}>조회구분</div>
                <div className={styles.searchInputContainer}>
                  <div className={styles.selectWrapper}>
                    <select
                      className={styles.searchTypeSelect}
                      value={filterValues.searchType}
                      onChange={(e) => handleFilterChange('searchType', Number(e.target.value))}
                    >
                      <option value={0}>제목</option>
                      <option value={1}>상신자</option>
                    </select>
                    <div className={styles.chevronIcon}>
                      <img src={chevronDownSmIcon} alt="" />
                    </div>
                  </div>
                  <input
                    type="text"
                    className={styles.searchInput}
                    value={filterValues.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                  <button type="button" className={styles.searchButton} onClick={handleSearch}>
                    검색
                  </button>
                </div>
              </div>

              {/* 하단 구분선 */}
              <div className={styles.filterDivider} />
            </div>

            {/* 문서 목록 섹션 */}
            <div className={styles.documentListSection}>
              <h3 className={styles.sectionTitle}>문서 목록</h3>
              <div className={styles.tableContainer}>
                {isLoading ? (
                  <div className={styles.loadingMessage}>로딩 중...</div>
                ) : availableDocList.length > 0 ? (
                  <div className={styles.tableWrapper}>
                    {/* 체크박스 컬럼 */}
                    <div className={styles.tableColumnCheckbox}>
                      <div className={styles.tableHeaderRow} />
                      <div
                        className={styles.tableHeader}
                        role="presentation"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={
                            availableDocList.length > 0 &&
                            availableDocList.every((doc) =>
                              checkedDocuments.find((item) => item.idx === doc.idx),
                            )
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              // 전체 선택
                              const newChecked = availableDocList.filter(
                                (doc) => !checkedDocuments.find((item) => item.idx === doc.idx),
                              );
                              setCheckedDocuments((prev) => [...prev, ...newChecked]);
                            } else {
                              // 전체 해제 (현재 목록에 있는 것만 해제)
                              const currentIds = availableDocList.map((doc) => doc.idx);
                              setCheckedDocuments((prev) =>
                                prev.filter((item) => !currentIds.includes(item.idx)),
                              );
                            }
                          }}
                          className={styles.checkbox}
                        />
                      </div>
                      {availableDocList.map((doc, index) => {
                        const isChecked = checkedDocuments.find((item) => item.idx === doc.idx);
                        const isSelected = selectedIdxList.includes(doc.idx);
                        return (
                          <div
                            key={doc.idx || index}
                            className={isSelected ? styles.selectedRow : ''}
                          >
                            <div className={styles.cellDivider} />
                            <div
                              className={styles.tableCell}
                              role="presentation"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={!!isChecked || isSelected}
                                onChange={() => handleCheckboxToggle(doc)}
                                className={styles.checkbox}
                                disabled={isSelected}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* 문서번호 컬럼 */}
                    <div className={styles.tableColumnNumber}>
                      <div className={styles.tableHeaderRow} />
                      <div className={styles.tableHeader}>문서번호</div>
                      {availableDocList.map((doc, index) => (
                        <div
                          key={doc.idx || index}
                          className={selectedIdxList.includes(doc.idx) ? styles.selectedRow : ''}
                        >
                          <div className={styles.cellDivider} />
                          <div
                            className={
                              onDocumentRowClick
                                ? `${styles.tableCell} ${styles.clickableCell}`
                                : styles.tableCell
                            }
                            role={onDocumentRowClick ? 'button' : undefined}
                            tabIndex={onDocumentRowClick ? 0 : undefined}
                            onClick={
                              onDocumentRowClick ? () => handleDocumentRowClick(doc) : undefined
                            }
                            onKeyDown={
                              onDocumentRowClick
                                ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleDocumentRowClick(doc);
                                    }
                                  }
                                : undefined
                            }
                          >
                            {doc.docNumber || '-'}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* 문서유형 컬럼 */}
                    <div className={styles.tableColumnType}>
                      <div className={styles.tableHeaderRow} />
                      <div className={styles.tableHeader}>문서유형</div>
                      {availableDocList.map((doc, index) => (
                        <div
                          key={doc.idx || index}
                          className={selectedIdxList.includes(doc.idx) ? styles.selectedRow : ''}
                        >
                          <div className={styles.cellDivider} />
                          <div
                            className={
                              onDocumentRowClick
                                ? `${styles.tableCell} ${styles.clickableCell}`
                                : styles.tableCell
                            }
                            role={onDocumentRowClick ? 'button' : undefined}
                            tabIndex={onDocumentRowClick ? 0 : undefined}
                            onClick={
                              onDocumentRowClick ? () => handleDocumentRowClick(doc) : undefined
                            }
                            onKeyDown={
                              onDocumentRowClick
                                ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleDocumentRowClick(doc);
                                    }
                                  }
                                : undefined
                            }
                          >
                            {doc.docType || '-'}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* 제목 컬럼 */}
                    <div className={styles.tableColumnTitle}>
                      <div className={styles.tableHeaderRow} />
                      <div className={styles.tableHeader}>제목</div>
                      {availableDocList.map((doc, index) => (
                        <div
                          key={doc.idx || index}
                          className={selectedIdxList.includes(doc.idx) ? styles.selectedRow : ''}
                        >
                          <div className={styles.cellDivider} />
                          <div
                            className={
                              onDocumentRowClick
                                ? `${styles.tableCell} ${styles.clickableCell}`
                                : styles.tableCell
                            }
                            role={onDocumentRowClick ? 'button' : undefined}
                            tabIndex={onDocumentRowClick ? 0 : undefined}
                            onClick={
                              onDocumentRowClick ? () => handleDocumentRowClick(doc) : undefined
                            }
                            onKeyDown={
                              onDocumentRowClick
                                ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleDocumentRowClick(doc);
                                    }
                                  }
                                : undefined
                            }
                          >
                            <span className={styles.cellTitleText}>{doc.title || '-'}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* 상신자 컬럼 */}
                    <div className={styles.tableColumnSender}>
                      <div className={styles.tableHeaderRow} />
                      <div className={styles.tableHeader}>상신자</div>
                      {availableDocList.map((doc, index) => (
                        <div
                          key={doc.idx || index}
                          className={selectedIdxList.includes(doc.idx) ? styles.selectedRow : ''}
                        >
                          <div className={styles.cellDivider} />
                          <div
                            className={
                              onDocumentRowClick
                                ? `${styles.tableCell} ${styles.clickableCell}`
                                : styles.tableCell
                            }
                            role={onDocumentRowClick ? 'button' : undefined}
                            tabIndex={onDocumentRowClick ? 0 : undefined}
                            onClick={
                              onDocumentRowClick ? () => handleDocumentRowClick(doc) : undefined
                            }
                            onKeyDown={
                              onDocumentRowClick
                                ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleDocumentRowClick(doc);
                                    }
                                  }
                                : undefined
                            }
                          >
                            {doc.name || '-'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={styles.emptyMessage}>-</div>
                )}
              </div>
            </div>

            {/* 추가 버튼 */}
            <div className={styles.addButtonContainer}>
              <button type="button" className={styles.addButton} onClick={handleAdd}>
                추가
              </button>
            </div>

            <div className={styles.selectedDocumentsSection}>
              <h3 className={styles.sectionTitle}>선택된 문서</h3>
              <div className={styles.tableContainer}>
                <div className={styles.tableWrapper}>
                  {/* 문서번호 컬럼 */}
                  <div className={styles.tableColumnNumber}>
                    <div className={styles.tableHeaderRow} />
                    <div className={styles.tableHeader}>문서번호</div>
                    {tempSelected.map((doc, index) => (
                      <div key={doc.idx || index}>
                        <div className={styles.cellDivider} />
                        <div className={styles.tableCell}>{doc.docNumber || '-'}</div>
                      </div>
                    ))}
                  </div>

                  {/* 문서유형 컬럼 */}
                  <div className={styles.tableColumnType}>
                    <div className={styles.tableHeaderRow} />
                    <div className={styles.tableHeader}>문서유형</div>
                    {tempSelected.map((doc, index) => (
                      <div key={doc.idx || index}>
                        <div className={styles.cellDivider} />
                        <div className={styles.tableCell}>{doc.docType || '-'}</div>
                      </div>
                    ))}
                  </div>

                  {/* 제목 컬럼 */}
                  <div className={styles.tableColumnTitle}>
                    <div className={styles.tableHeaderRow} />
                    <div className={styles.tableHeader}>제목</div>
                    {tempSelected.map((doc, index) => (
                      <div key={doc.idx || index}>
                        <div className={styles.cellDivider} />
                        <div className={styles.tableCell}>
                          <span className={styles.cellTitleText}>{doc.title || '-'}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* 상신자 컬럼 */}
                  <div className={styles.tableColumnSender}>
                    <div className={styles.tableHeaderRow} />
                    <div className={styles.tableHeader}>상신자</div>
                    {tempSelected.map((doc, index) => (
                      <div key={doc.idx || index}>
                        <div className={styles.cellDivider} />
                        <div className={styles.tableCell}>{doc.name || '-'}</div>
                      </div>
                    ))}
                  </div>

                  {/* 삭제 버튼 컬럼 */}
                  <div className={styles.tableColumnDelete}>
                    <div className={styles.tableHeaderRow} />
                    <div className={styles.tableHeader}></div>
                    {tempSelected.map((doc, index) => (
                      <div key={doc.idx || index}>
                        <div className={styles.cellDivider} />
                        <div className={styles.tableCell}>
                          <button
                            type="button"
                            className={styles.deleteButton}
                            onClick={() => handleRemoveSelected(doc)}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {tempSelected.length === 0 && (
                  <div className={styles.emptyMessage}>선택된 문서가 없습니다.</div>
                )}
              </div>
            </div>
          </div>

          {/* 모달 푸터 */}
          <div className={styles.modalFooter}>
            <button type="button" className={styles.cancelButton} onClick={onClose}>
              취소
            </button>
            <button type="button" className={styles.saveButton} onClick={handleSave}>
              저장
            </button>
          </div>
        </div>
      </div>

      {/* Alert 컴포넌트 */}
      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
    </>
  );
}

export default ReferenceDocumentModal;
